import { REPORT_NAMINGS, REPORT_TYPES } from '../../../consts/reportTypes';
import {
  extractionNotStarted,
  isReportGenerated,
  mappingProgressSubLabelForPeriod
} from '../../../utils/periodProgress';
import { firstUnmetGate, stageGateFor } from '../../../utils/stageGating';
import {
  getStatus,
  getStatusForConversionReportType,
  isFromScratch,
  isMinimalFiling,
  isNilReturnPeriod,
  statusKeys
} from '../Quarters/data';

/**
 * What a period's steps say on the V2 project list.
 *
 * The periods page draws every cell as a blue underlined link with an arrow after it — eight of
 * them in a row, plus a repeated "Add Jurisdictions" in each step still out of reach. Read down a
 * list that is already dense with rows, it is all emphasis and no information.
 *
 * So the cells are derived here instead, as plain values: a count where the step counts something,
 * one word where it has a state, and a verb only on the step that is actually next. Colour is spent
 * on that one verb and on failure; everything else is type. The step's own V2 page is where each
 * one leads.
 */

const DONE = 'Completed';
export const NOT_STARTED = 'Not started';

/* Which step a period is on, the way the periods page works it out. */
const CURRENT_STEP = {
  [statusKeys.AIF]: 'aif',
  [statusKeys.JURISDICTION]: 'jurisdictions',
  [statusKeys.FILES]: 'files',
  [statusKeys.EXTRACTION]: 'extraction',
  [statusKeys.EXTRACTION_IN_PROGRESS]: 'extraction',
  [statusKeys.TRANSFORMATION]: 'transformation',
  [statusKeys.TRANSFORMATION_IN_PROGRESS]: 'transformation',
  [statusKeys.REPORT]: 'report',
  [statusKeys.APPROVAL]: 'approval',
  [statusKeys.SUBMISSION]: 'submission'
};

const idle = { value: '—', tone: 'idle' };

/* The order the pipeline runs in. Conversion files its source before its funds; everything else
   follows the Annex IV chain. */
const ORDER = {
  default: [
    'aif',
    'jurisdictions',
    'files',
    'mapping',
    'extraction',
    'lookup',
    'transformation',
    'output_data',
    'report',
    'approval',
    'submission'
  ],
  conversion: ['files', 'aif', 'jurisdictions', 'report', 'approval', 'submission']
};

const runState = stats => ({
  failed: stats?.failed_extractions_number || stats?.failed_transformations_number || 0,
  running: stats?.in_progress_extractions_number || stats?.in_progress_transformation_number || 0,
  done: stats?.successful_extractions_number || stats?.successful_transformations_number || 0,
  /* Sheets the run finished without: the periods page prints "2 not extracted" beside its
     "14 successful", and a bare "Finished" swallows both halves of that. */
  missed:
    stats?.not_completed_extractions_number || stats?.not_completed_transformations_number || 0
});

/** A step that runs something: extraction, transformation. */
const runStep = ({
  progress,
  stats,
  isNext,
  isPassed,
  verb,
  to,
  notStarted = false,
  hasActions = false
}) => {
  const { failed, running, done, missed } = runState(stats);

  /* Measured absence beats both the rolled-up status and the cursor's position. Without this a
     period standing past Extraction reads "Finished" over an extraction list holding none — the
     same claim the in-period stepper stopped making, and the two sit one click apart. */
  if (notStarted) return isNext || isPassed ? { value: verb, tone: 'next', to } : idle;

  /* `issues` marks the states the periods page opens its issues drawer for — the sheets that did
     not come out, listed and re-runnable, rather than the extraction page as a whole. */
  if (failed > 0) return { value: 'Failed', tone: 'attention', to, issues: true };
  if (running > 0) return { value: 'Running', tone: 'muted', to };
  /* A shortfall matters while the period is standing here. Once the pipeline has moved past — the
     report is generated, the thing that is left is submission — those two sheets are history, and
     printing them in red says the period needs attention it does not need. */
  if (missed > 0 && done > 0)
    return {
      value: `${done} of ${done + missed}`,
      tone: isPassed ? 'done' : 'attention',
      to,
      issues: true
    };
  if (missed > 0)
    return {
      value: `${missed} not done`,
      tone: isPassed ? 'done' : 'attention',
      to,
      issues: true
    };
  /* `isPassed` matters on older automations, where a period is plainly past extraction but its
     per-run statistics are empty: the periods page says "Finished" there and a dash would be a
     worse answer than the truth. */
  if (done > 0 || progress === DONE || isPassed || hasActions)
    return { value: 'Finished', tone: 'done', to };
  if (isNext) return { value: verb, tone: 'next', to };
  return idle;
};

/** A step that counts something: funds, jurisdictions, files. */
const countStep = ({ count, isNext, isPassed, verb, to }) => {
  if (count > 0) return { value: String(count), tone: 'done', to };
  if (isPassed) return { value: '0', tone: 'done', to };
  if (isNext) return { value: verb, tone: 'next', to };
  return idle;
};

/* Nil Return periods have nothing to upload/extract/transform — same word and same hover tooltip
   the classic page's NilReturnCell shows. `idle` tone: plain grey text, matching every stage the
   same way instead of extraction's `done` green. `locked`/`lockReason` reuse the gate-blocked
   step's own tooltip mechanism (see `locked()` below) to pop up that message. No `to`: there's
   nothing behind this step to navigate to. `nilReturnLock` tells a locked cell apart from a
   gate-blocked one — PeriodPipeline draws a blocked cell as a bare em dash, which would swallow
   the "Nil Return" word itself; this flag is how it knows to show the value instead. */
const nilReturnStep = message => ({
  value: 'Nil Return',
  tone: 'idle',
  locked: true,
  nilReturnLock: true,
  nilReturnMessage: message,
  lockReason: `This period is a Nil Return — ${message}.`
});

export const buildPeriodSteps = ({
  period,
  reportType,
  workflowId,
  /* The two stages the stepper shows conditionally, so this shows them on the same condition: Mapping
     needs `v2_ui` (it is a redesign-only page), Lookup the company's `lookup_table` flag. Both default
     to off, which is what `periodHeadline` wants — it summarises what to DO, and neither of these ever
     asks for anything. */
  hasMapping = false,
  hasLookup = false
}) => {
  const isConversion = reportType === REPORT_TYPES.CONVERSION;
  const isCustom = reportType === REPORT_TYPES.CUSTOM;
  const isNilReturn = reportType === REPORT_TYPES.ANNEX_IV && isNilReturnPeriod(period);
  const minimal = isMinimalFiling(period);

  const status = isConversion
    ? getStatusForConversionReportType(period)
    : getStatus(period, reportType);
  const gate = firstUnmetGate({ period, reportType, hasMapping });

  /* Everything before the step the period is standing on has been done, whatever its own fields
     say — that is what standing here means. */
  const order = isConversion ? ORDER.conversion : ORDER.default;
  const standingAt = status === statusKeys.END ? order.length : order.indexOf(CURRENT_STEP[status]);
  const gateAt = gate ? order.indexOf(gate.id) : -1;
  const heldBack = gateAt >= 0 && (standingAt < 0 || gateAt < standingAt);
  const cursor = heldBack ? gate.id : CURRENT_STEP[status];
  const next = key => !minimal && cursor === key;

  /* A finished period stands past the end of the chain, not nowhere: without this, everything it
     did shows as a dash the moment it is submitted. */
  const at = heldBack ? gateAt : standingAt;
  const passed = key => !minimal && at >= 0 && order.indexOf(key) < at;

  const period_ = `${workflowId}/${period.id}`;
  const fundNoun = REPORT_NAMINGS.FUND?.[reportType] || 'AIF';
  const jurisdictionNoun = REPORT_NAMINGS.JURISDICTION?.[reportType] || 'Jurisdictions';

  const reportDone = isReportGenerated(period);
  /* Or the period has moved past it, which is the same fact stated by where it stands: some filed
     periods never had the field set to Completed at all. Everything downstream of the report — is
     it your turn to approve, is submission waiting on you — asks this rather than the field, or two
     periods filed the same way answer differently because one of them has a stale status. */
  const reportBehind = () => reportDone || passed('report');
  const commentsOpen = period.comments?.total_threads !== period.comments?.resolved_threads;

  const report = () => {
    const to = isCustom ? `/output/${period_}` : `/report-v2/${period_}`;

    if (reportBehind()) return { value: 'Generated', tone: 'done', to };
    if (period.report_task && period.report_task.status !== DONE)
      return { value: 'Generating…', tone: 'muted', to };
    if (next('report')) return { value: 'Generate', tone: 'next', to };
    return idle;
  };

  const approval = () => {
    /* Approved means the field says approved. It cannot be inferred from the period having moved
       on — a period can be submitted with approval never recorded. */
    if (period.approve_progress_status === DONE) return { value: 'Approved', tone: 'done' };
    if (period.approve_progress_status && period.approve_progress_status !== NOT_STARTED)
      return { value: 'Pending', tone: 'muted' };
    if (reportBehind()) return { value: 'Send', tone: commentsOpen ? 'muted' : 'next' };
    return idle;
  };

  const submission = () => {
    if (period.submit_progress_status === DONE) return { value: 'Submitted', tone: 'done' };
    /* The periods page says why the step cannot start; a dash says only that it has not. */
    if (reportBehind() && commentsOpen) {
      const open = (period.comments?.total_threads || 0) - (period.comments?.resolved_threads || 0);
      /* Where the threads are: the report page. Saying a period is held up by a comment and giving
         no way to read it was the worst of both — the old page at least linked "1 New Comment". */
      return {
        value: open > 0 ? `Resolve ${open} comment${open === 1 ? '' : 's'}` : 'Resolve comments',
        tone: 'comment',
        to: isCustom ? `/output-tables/${period_}` : `/report-v2/${period_}`
      };
    }
    if (period.submit_progress_status && period.submit_progress_status !== NOT_STARTED)
      return { value: 'In progress', tone: 'muted' };
    /* What it is waiting for, not merely that it has not happened. But once approval is under way —
       pending, not only completed — the period can be marked submitted, which is exactly when the
       periods page swaps "Approval Pending" for its "Not submitted" menu. */
    const approvalStarted =
      period.approve_progress_status && period.approve_progress_status !== NOT_STARTED;

    if (reportBehind() && !approvalStarted) return { value: 'Approval pending', tone: 'muted' };
    if (reportBehind()) return { value: 'Not submitted', tone: 'muted' };
    return idle;
  };

  /* The two stages that had no cell here. Both are pages in the chain, and a chain that skips two of
     its links reads as if they are not part of the work. */
  const mapping = () => {
    const to = `/setup/${period_}`;
    /* Nil Return periods never get files to map — nothing is left "Not started" here. */
    if (isNilReturn) return nilReturnStep('no mapping required');
    // Blocked until Source Files is done, not merely reached — "Upload Files" still on screen
    // there means the sheets this step would read don't exist yet. No `to`: same as every other
    // step still out of reach, this one isn't a link yet either.
    if (!passed('files')) return idle;
    /* Same rule and same words as the in-period stepper — the two sit one click apart, and reading
       `category_mapping_progress_status` here while the stepper read extraction activity is what
       had one saying "Finished" and the other "In progress" for the same period. */
    const value = mappingProgressSubLabelForPeriod(period);
    return { value, tone: value === 'Finished' ? 'done' : 'muted', to };
  };

  const lookup = () => {
    const to = `/data-extraction/${period_}?tab=lookup_tables`;
    /* Nil Return periods skip Lookup Tables entirely, same as Files/Extraction/Transformation. */
    if (isNilReturn) return nilReturnStep('no lookup required');
    if (period.lookup_progress_status === DONE) return { value: 'Finished', tone: 'done', to };
    /* Before the field is consulted, not after. The backend completes Lookup and Output Data on its
       own as the report is generated and leaves `lookup_progress_status` sitting at "In progress" —
       the same trap `report_progress_status` carries. Reading the field first put "In progress" in
       this cell next to a generated report, and next to a stepper reading Finished for the very same
       stage: where the period stands is the harder fact, so it goes first. */
    if (passed('lookup') || reportBehind()) return { value: 'Finished', tone: 'done', to };
    if (period.lookup_progress_status && period.lookup_progress_status !== NOT_STARTED)
      return { value: 'In progress', tone: 'muted', to };
    return { ...idle, to };
  };

  /* Where the critical issues that lock Report live — without this cell the lock pointed at nothing. */
  const outputData = () => {
    const to = `/output-tables/${period_}`;
    if (reportBehind() || period.report_task) return { value: 'Finished', tone: 'done', to };
    const critical = period.criticalIssueCount;
    if (critical > 0)
      return {
        value: `${critical} critical issue${critical === 1 ? '' : 's'}`,
        tone: 'attention',
        to
      };
    // The backend completes Output data as transformation finishes — the stepper reads it the same way.
    if (next('report')) return { value: 'Finished', tone: 'done', to };
    return idle;
  };

  const steps = [
    {
      key: 'aif',
      caption: `${fundNoun}s`,
      ...(minimal
        ? idle
        : countStep({
            count: period.fund_count,
            isNext: next('aif'),
            isPassed: passed('aif'),
            verb: 'Add',
            to: `/project/quaters/${workflowId}/${period.id}/add-aifs`
          }))
    },
    {
      key: 'jurisdictions',
      caption: jurisdictionNoun,
      ...(minimal
        ? idle
        : countStep({
            count: period.jurisdiction_count,
            isNext: next('jurisdictions'),
            isPassed: passed('jurisdictions'),
            verb: 'Add',
            to: `/project/quaters/${workflowId}/${period.id}/jurisdictions`
          }))
    },
    {
      key: 'files',
      caption: 'Source files',
      ...(minimal
        ? idle
        : isNilReturn
          ? nilReturnStep('no source files required')
          : countStep({
              count: period.file_count,
              isNext: next('files'),
              isPassed: passed('files'),
              verb: 'Add',
              to: `/project/quaters/${workflowId}/${period.id}/upload-files`
            }))
    },
    { key: 'mapping', caption: 'Mapping', ...(minimal ? idle : mapping()) },
    {
      key: 'extraction',
      caption: 'Extraction',
      ...(minimal
        ? idle
        : isNilReturn
          ? nilReturnStep('no extraction required')
          : runStep({
              progress: period.extraction_progress_status,
              stats: period.extraction_stats,
              isNext: next('extraction'),
              isPassed: passed('extraction'),
              notStarted: extractionNotStarted(period),
              verb: 'Start',
              to: `/data-extraction/${period_}?tab=extraction`
            }))
    },
    { key: 'lookup', caption: 'Lookup', ...(minimal ? idle : lookup()) },
    {
      key: 'transformation',
      caption: 'Transformation',
      ...(minimal
        ? idle
        : isNilReturn
          ? nilReturnStep('no transformation required')
          : runStep({
              progress: period.transformation_progress_status,
              stats: period.transformation_stats,
              isNext: next('transformation'),
              isPassed: passed('transformation'),
              hasActions: isFromScratch(period) && period.transformationCount > 0,
              verb: 'Start',
              to: `/data-extraction/${period_}?tab=transformation`
            }))
    },
    {
      key: 'output_data',
      caption: 'Output data',
      ...(minimal ? idle : isNilReturn ? nilReturnStep('no output data required') : outputData())
    },
    { key: 'report', caption: isCustom ? 'Output table' : 'Report', ...report() },
    { key: 'approval', caption: 'Approval', ...approval() },
    { key: 'submission', caption: 'Submission', ...submission() }
  ];

  const locked = step => {
    const blocking = stageGateFor(step.key, { period, reportType, hasMapping });
    if (!blocking) return step;
    return { ...step, ...idle, to: undefined, locked: true, lockReason: blocking.message };
  };

  /* The same two rearrangements the periods page makes to its columns. */
  if (isConversion) {
    const order = ['files', 'aif', 'jurisdictions', 'report', 'approval', 'submission'];
    return order
      .map(key => steps.find(step => step.key === key))
      .filter(Boolean)
      .map(locked);
  }

  const dropped = [
    !hasMapping && 'mapping',
    !hasLookup && 'lookup',
    ...(isCustom ? ['aif', 'jurisdictions', 'output_data', 'approval', 'submission'] : [])
  ].filter(Boolean);

  return steps.filter(step => !dropped.includes(step.key)).map(locked);
};

/**
 * What a period's tile says on a project card.
 *
 * One line, and it is the same judgement the grid makes — the step that needs attention if there is
 * one, otherwise the step that is next, otherwise where the period ended up. The card is a summary,
 * so it says what to do rather than how far along things are; the table underneath has the detail.
 */
export const periodHeadline = ({ period, reportType, workflowId }) => {
  const steps = buildPeriodSteps({ period, reportType, workflowId });

  /* `key` is which step this is about — the caller needs it to say the same thing in its own words:
     a glyph, a verb on a button. The text alone would have to be parsed back. */
  const attention = steps.find(step => step.tone === 'attention');
  if (attention)
    return {
      text: attention.value === 'Failed' ? `${attention.caption} failed` : attention.value,
      tone: 'attention',
      key: attention.key,
      to: attention.to
    };

  /* Nothing failed, but something is waiting on a person rather than on the platform. */

  const next = steps.find(step => step.tone === 'next' || step.tone === 'comment');
  if (next) {
    const NEXT = {
      aif: caption => `Add ${caption}`,
      jurisdictions: caption => `Add ${caption.toLowerCase()}`,
      files: () => 'Add source files',
      extraction: () => 'Start extraction',
      transformation: () => 'Start transformation',
      report: caption => `Generate ${caption.toLowerCase()}`,
      approval: () => 'Send for approval',
      submission: () => 'Submit'
    };
    return {
      text: (NEXT[next.key] || (caption => caption))(next.caption),
      tone: 'next',
      key: next.key,
      to: next.to
    };
  }

  const submission = steps.find(step => step.key === 'submission');
  if (submission?.value === 'Submitted')
    return { text: 'Submitted', tone: 'idle', key: 'submission' };
  /* The one thing left to do, even though it happens in a menu rather than on a page — the periods
     page calls this period's state "Submit" and it is right: nothing else is waiting. */
  if (submission?.value === 'Not submitted')
    return { text: 'Submit', tone: 'next', key: 'submission', to: submission.to };

  const running = steps.find(step => step.tone === 'muted');
  if (running)
    return {
      text: `${running.caption} ${running.value.toLowerCase()}`,
      tone: 'muted',
      key: running.key
    };

  return { text: 'Nothing to do', tone: 'idle' };
};

/* The same step as a verb on a button. The headline is a sentence for reading — "Send for approval
   — ICG Europe" — and a button beside it wants the one word that is the doing part. Null where
   there is nothing for a person to do: a period that is running, filed, or not yet started is not
   an action, and a button that says "Open" is not an action either. */
const VERBS = {
  aif: 'Add',
  jurisdictions: 'Add',
  files: 'Upload',
  extraction: 'Start',
  transformation: 'Start',
  report: 'Generate',
  approval: 'Send',
  submission: 'Submit'
};

export const nextVerb = head => {
  if (!head) return null;
  if (head.key === 'newPeriod') return 'Start';
  if (head.tone === 'attention') return 'Review';
  if (head.tone === 'comment') return 'Open';
  if (head.tone !== 'next') return null;
  return VERBS[head.key] || null;
};

/* Q1 < Q2 < Q3 < Q4, H1 < H2, and a year on its own. Enough to put a project's periods in order;
   the API returns them in whatever order it kept them in. */
const WITHIN_YEAR = { Q1: 1, Q2: 2, Q3: 3, Q4: 4, H1: 1, H2: 2, Y1: 1 };

/**
 * Newest first.
 *
 * Which matters more than it looks: a period that has a later period after it was filed — nobody
 * opens next quarter with last quarter outstanding — so the latest one is the only one that can
 * still be work. The rest are history, whatever the platform recorded about them.
 */
export const byNewest = (a, b) => {
  const year = Number(b.year) - Number(a.year);
  if (year) return year;
  return (WITHIN_YEAR[b.period] || 0) - (WITHIN_YEAR[a.period] || 0);
};

/** Where a period's report lives — the last thing there is to look at once the work is done. */
/* The output table holds both designs at its classic address; the report is the one stage that
   still has two, because the classic one wants a regulator and a member state in its path and this
   has neither. The redesigned report picks its own jurisdiction, so it is the cheaper link. */
export const reportRoute = ({ reportType, workflowId, periodId }) =>
  reportType === REPORT_TYPES.CUSTOM
    ? `/output-tables/${workflowId}/${periodId}`
    : `/report-v2/${workflowId}/${periodId}`;

/**
 * Open comment threads on a period, and the page they live on.
 *
 * `isNew` follows the periods page: it calls threads new while nothing has been resolved on them.
 * Once somebody has resolved one, the conversation has been read and worked on, and the count stops
 * being an announcement.
 */
export const openComments = ({ period, reportType, workflowId }) => {
  const total = period.comments?.total_threads || 0;
  const resolved = period.comments?.resolved_threads || 0;
  const open = total - resolved;
  if (open <= 0) return null;

  return {
    count: open,
    isNew: resolved === 0,
    to: reportRoute({ reportType, workflowId, periodId: period.id })
  };
};

/* ── The filing deadline ─────────────────────────────────────────────────────
   Computed by the backend and delivered on the period itself, under `deadlines`. Nothing here
   invents a date: the regulatory rules — one calendar month after the period closes for Annex IV
   and Conversion, per Article 110(4); fifteen days more for a fund of funds; 15, 60 or 120 days for
   the three Form PF filer categories; and the per-jurisdiction weekend roll that moves a date off a
   Saturday in Sweden and back off one in Denmark — all live there, worked out against the period's
   own jurisdictions, funds and adviser assets.

   The shape is one object per regime, only one of them filled:

     deadlines.annex_iv = { jurisdictions: [{ reporting_member_state, country_name, weekend_rule,
                            submission_deadline, submission_deadline_fund_of_funds }],
                            has_fund_of_funds }
     deadlines.form_pf  = { filers: [{ category, submission_deadline }], is_predicted }

   A custom workflow has no deadline rule at all and sends `null`, which is why an absent object is
   read as "no date" rather than as something to fill in. `end_date` is deliberately not consulted:
   that is when the reporting period closed, which is a different date from when the filing is due,
   and labelling one as the other would be a lie the size of a fine. */

/* A date-only string from the API — `2026-10-31` — is a calendar date, not an instant. Handing it
   to `new Date` parses it as UTC midnight, which west of Greenwich is the day before; every
   comparison on this page is against local midnight, so it is built as a local date instead. */
const toLocalDate = value => {
  if (!value) return null;
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  const at = parts
    ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))
    : new Date(value);
  return Number.isNaN(at.getTime()) ? null : at;
};

/* The last day of the period the name describes. The nine-month X1/X2 periods end where the
   backend's own mapping puts them — X1 in September, X2 in December. */
const PERIOD_END = {
  Q1: [2, 31],
  Q2: [5, 30],
  Q3: [8, 30],
  Q4: [11, 31],
  H1: [5, 30],
  H2: [11, 31],
  Y1: [11, 31],
  X1: [8, 30],
  X2: [11, 31]
};

/* Which period follows which, and when the year turns over with it. */
const NEXT_PERIOD = { Q1: 'Q2', Q2: 'Q3', Q3: 'Q4', Q4: 'Q1', H1: 'H2', H2: 'H1', Y1: 'Y1' };
const WRAPS_YEAR = ['Q4', 'H2', 'Y1'];

/** The period that comes after this one, by name — the project's own cadence continued. */
export const nextPeriodAfter = period => {
  const name = NEXT_PERIOD[period?.period];
  const year = Number(period?.year);
  if (!name || !year) return null;
  return { period: name, year: year + (WRAPS_YEAR.includes(period.period) ? 1 : 0) };
};

/** The day a period closes — the day its filing window opens. */
export const periodEndOf = period => {
  const end = PERIOD_END[period?.period];
  const year = Number(period?.year);
  if (!end || !year) return null;
  return new Date(year, end[0], end[1]);
};

/* Whole calendar months, the way the regulation counts them and the way the backend's `add_months`
   does: one month after 30 June is 31 July, not 30 July. Used only by the projection below —
   every date on a period that exists is arithmetic the backend has already done. */
const addMonths = (at, months) => {
  const lastOfMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const isMonthEnd = at.getDate() === lastOfMonth(at.getFullYear(), at.getMonth());
  const total = at.getMonth() + months;
  const year = at.getFullYear() + Math.floor(total / 12);
  const month = ((total % 12) + 12) % 12;
  const last = lastOfMonth(year, month);
  return new Date(year, month, isMonthEnd ? last : Math.min(at.getDate(), last));
};

/* The one date on this page the API cannot supply: the queue offers the period nobody has created
   yet, and a period that does not exist has no jurisdictions to roll a date against and no funds to
   extend it for. So it is projected from the base rule alone — one month after the period closes —
   and carried as `isProjected` so whatever shows it can say so. It is never used for a period that
   exists: those show the backend's dates or no date at all. */
const projectedDeadline = period => {
  const end = periodEndOf(period);
  return end ? addMonths(end, 1) : null;
};

/* What each regime calls the thing a date belongs to. Annex IV files once per jurisdiction, Form PF
   once per filer category the firm falls into, and a period holding a fund of funds carries its
   fifteen-day extension alongside the ordinary date rather than instead of it. */
const annexIVEntries = annexIV =>
  (annexIV?.jurisdictions || []).flatMap(row => {
    const code = row.reporting_member_state;
    const name = row.country_name || code;
    const at = toLocalDate(row.submission_deadline);
    const extended = annexIV.has_fund_of_funds
      ? toLocalDate(row.submission_deadline_fund_of_funds)
      : null;

    return [
      at && { key: code, label: name, name, code, at, kind: 'jurisdiction' },
      extended && {
        key: `${code}:fof`,
        label: `${name} · funds of funds`,
        name,
        code,
        at: extended,
        kind: 'fundOfFunds'
      }
    ].filter(Boolean);
  });

const formPFEntries = formPF =>
  (formPF?.filers || [])
    .map(row => {
      const at = toLocalDate(row.submission_deadline);
      return (
        at && { key: row.category, label: row.category, name: row.category, at, kind: 'filer' }
      );
    })
    .filter(Boolean);

/**
 * Every date a period is actually due on, one entry per filing obligation.
 *
 * A period is rarely one deadline. Annex IV is filed per jurisdiction, and one period can be due on
 * three different days because Denmark rolls a weekend date back and Sweden rolls it forward; Form
 * PF can catch a firm in two filer categories at once, one at 60 days and one at 15. So the list is
 * what the period actually is, and the single date a row leads with is a summary of it.
 */
export const deadlineEntries = period => {
  const deadlines = period?.deadlines;
  if (!deadlines) return [];
  if (deadlines.annex_iv) return annexIVEntries(deadlines.annex_iv);
  if (deadlines.form_pf) return formPFEntries(deadlines.form_pf);
  return [];
};

/**
 * The date the period is next due on.
 *
 * The earliest of them, and a fund-of-funds date is not among the candidates: that is fifteen days
 * of grace for some of the funds in the period, not a later date for the period. Leading with it
 * would move the date everything else in the period is still due on.
 */
export const deadlineOf = period => {
  if (!period) return null;

  /* A period that exists says what it is due; a period the queue is proposing has to be projected,
     because it has nothing in the platform yet to compute a date from. */
  if (!period.deadlines) return period.id ? null : projectedDeadline(period);

  const entries = deadlineEntries(period);
  const ordinary = entries.filter(entry => entry.kind !== 'fundOfFunds');
  const from = ordinary.length ? ordinary : entries;
  return from.reduce((first, entry) => (!first || entry.at < first ? entry.at : first), null);
};

/* ── The filing window ───────────────────────────────────────────────────────
   A period closes and the report is due a fixed span later; the work happens between those two
   dates and nowhere else. Annex IV is a calendar month after the reporting period ends; Form PF is
   60 days after quarter end for a large hedge fund adviser and 120 for everyone else — so the same
   number of days left means different things in the two regimes.

   This is only the grouping threshold now: how far ahead a filing counts as open rather than ahead.
   The dates themselves come from the period.

   It is also why "overdue" is the wrong word on this screen. A filing has a date, the date is in
   the calendar a year ahead, and missing one is not a late task — it is a reportable failure. What
   a screen can usefully say is how much of the window is left. */
const WINDOW_DAYS = {
  [REPORT_TYPES.ANNEX_IV]: 31,
  [REPORT_TYPES.CONVERSION]: 31,
  [REPORT_TYPES.FORM_PF]: 60,
  [REPORT_TYPES.CUSTOM]: 31
};

export const filingWindowDays = reportType => WINDOW_DAYS[reportType] ?? 31;

/* A date as this page writes it: the year only once it is not this one. Every Q2 in the book is due
   on 31 July, so a column of periods across four years printed "31 Jul" four times and left the
   reader to work out which was which. */
const writeDate = (at, now) =>
  at.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(at.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' })
  });

// "30–31 Jan", "30 Jan – 2 Feb"; the year once, at the end, when it is not this one.
export const writeRange = (first, last, now) => {
  if (first.getTime() === last.getTime()) return writeDate(first, now);
  const sameYear = first.getFullYear() === last.getFullYear();
  if (sameYear && first.getMonth() === last.getMonth())
    return `${first.getDate()}–${writeDate(last, now)}`;
  const head = sameYear
    ? first.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    : writeDate(first, now);
  return `${head} – ${writeDate(last, now)}`;
};

// How far off a date is; the countdown only while it is one. Amber in the last week.
const countdown = (at, now) => {
  const startOfDay = date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const days = Math.round((startOfDay(at) - startOfDay(now)) / 86400000);
  const near = Math.abs(days) <= 60;
  if (days < 0) {
    const ago = `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`;
    return { days, relative: near ? ago : null, tone: 'idle' };
  }
  const ahead =
    days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} day${days === 1 ? '' : 's'}`;
  return { days, relative: near ? ahead : null, tone: days <= 7 ? 'warning' : 'idle' };
};

/**
 * When it is due, and how long that is.
 *
 * The date leads and the countdown follows it: this is regulated filing, where people work to a
 * date in the calendar and know it by heart, and "in 20 days" alone makes them do the arithmetic
 * backwards. Amber in the last week, grey otherwise — deadlines carry no red.
 *
 * `entries` carries everything else the period is due on, so anything with room for it can say
 * which jurisdictions share the leading date and which sit a day either side of it. `isPredicted`
 * is the backend's own word about a Form PF classification inherited from an earlier period because
 * this one has no adviser assets of its own yet; `isProjected` marks the one date this page works
 * out itself, for a period nobody has created.
 */
export const deadlineLabel = (period, now = new Date()) => {
  const at = deadlineOf(period);
  if (!at) return null;

  const date = writeDate(at, now);
  const entries = deadlineEntries(period)
    .map(entry => ({ ...entry, date: writeDate(entry.at, now) }))
    .sort((left, right) => left.at - right.at);
  /* The leading date is the whole story only while every obligation shares it. */
  const spread = entries.some(entry => entry.at.getTime() !== at.getTime());
  const isPredicted = !!period?.deadlines?.form_pf?.is_predicted;
  const isProjected = !period?.deadlines;
  // Different days read as a range; the fund-of-funds extension stays out of it.
  const ordinary = entries.filter(entry => entry.kind !== 'fundOfFunds');
  const last = ordinary.length ? ordinary[ordinary.length - 1].at : at;
  const span = writeRange(at, last > at ? last : at, now);
  const { days, relative, tone } = countdown(at, now);

  return {
    at,
    days,
    date,
    entries,
    spread,
    isPredicted,
    isProjected,
    relative,
    tone,
    span,
    text: days < 0 ? `Was due ${span}` : `Due ${span}`
  };
};

// For a chip with no room for names: the country due first, and how many follow.
export const deadlineWho = due => {
  const codes = [
    ...new Set(
      (due?.entries ?? [])
        .filter(entry => entry.kind === 'jurisdiction' && entry.code)
        .map(entry => entry.code)
    )
  ];
  if (codes.length === 0) return null;
  return codes.length === 1 ? codes[0] : `${codes[0]} +${codes.length - 1}`;
};

// One entry per day with the countries due on it; funds of funds apart.
export const deadlineDays = entries => {
  const group = list =>
    list.reduce((days, entry) => {
      const item = { code: entry.code || null, name: entry.name };
      const last = days[days.length - 1];
      if (last && last.at.getTime() === entry.at.getTime()) last.items.push(item);
      else days.push({ at: entry.at, date: entry.date, items: [item] });
      return days;
    }, []);
  return {
    days: group(entries.filter(entry => entry.kind !== 'fundOfFunds')),
    fundOfFunds: group(entries.filter(entry => entry.kind === 'fundOfFunds'))
  };
};

// One member state's own date, for a page about that country's filing.
export const jurisdictionDeadline = (period, code, now = new Date()) => {
  const wanted = String(code || '').toUpperCase();
  const entry = deadlineEntries(period).find(
    item => item.kind === 'jurisdiction' && String(item.key).toUpperCase() === wanted
  );
  if (!entry) return null;
  const date = writeDate(entry.at, now);
  const { days, relative, tone } = countdown(entry.at, now);
  return {
    at: entry.at,
    days,
    date,
    name: entry.name,
    relative,
    tone,
    entries: [],
    spread: false,
    text: `${entry.name} · ${days < 0 ? 'was due' : 'due'} ${date}`
  };
};
